import { useTranslation } from 'react-i18next'
import { Building2, ChevronRight, History, LayoutDashboard, ListTree, Truck, Users } from 'lucide-react'
import { NavLink, useLocation, useParams } from 'react-router-dom'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible'
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarRail,
  SidebarSeparator,
  useSidebar
} from '@/components/ui/sidebar'
import { InstallationWorkspaceSwitcher } from '@/components/layout/installation-workspace-switcher'
import { OperatorsProfileDropdown } from '@/components/layout/operators-profile-dropdown'
import { usePermissions } from '@/shared/hooks/usePermissions'

export function OperatorsAppSidebar() {
  return (
    <Sidebar collapsible="icon" variant="inset">
      <SidebarHeader>
        <InstallationWorkspaceSwitcher />
      </SidebarHeader>
      <SidebarContent>
        <OperatorsNav />
      </SidebarContent>
      <SidebarFooter>
        <OperatorsProfileDropdown variant="sidebar" />
      </SidebarFooter>
      <SidebarRail />
    </Sidebar>
  )
}

function OperatorsNav() {
  const { t } = useTranslation('common')
  const { installationId } = useParams<{ installationId: string }>()
  const location = useLocation()
  const { setOpenMobile } = useSidebar()
  const { checkPermission } = usePermissions()
  const canManageOrganization = checkPermission('organization', 'update')
  const canManageUsers = checkPermission('organization', 'manage-users')
  const dashboardPath = `/dashboard/${installationId}`
  const suppliersPath = `${dashboardPath}/suppliers`
  const installationConfigurationBasePath = `${dashboardPath}/installation/configuration`
  const installationPrecursorCnPath = `${installationConfigurationBasePath}/precursor-cn-codes`
  const installationProducedCnPath = `${installationConfigurationBasePath}/produced-cn-codes`
  const installationFuelsPath = `${installationConfigurationBasePath}/fuels`
  const installationScrapCategoriesPath = `${installationConfigurationBasePath}/scrap-categories`
  const installationOtherMaterialPath = `${installationConfigurationBasePath}/other-material-categories`
  const organizationCompanyPath = `${dashboardPath}/organization/company`
  const usersPath = `${dashboardPath}/users`
  const organizationActivityPath = `${dashboardPath}/organization/activity`

  return (
    <>
      <SidebarGroup>
        <Collapsible defaultOpen className="group/collapsible">
          <SidebarGroupLabel asChild>
            <CollapsibleTrigger className="flex w-full items-center">
              {t('common:nav.menu')}
              <ChevronRight className="ms-auto transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90 rtl:rotate-180" />
            </CollapsibleTrigger>
          </SidebarGroupLabel>
          <CollapsibleContent>
            <SidebarGroupContent>
              <SidebarMenu>
                <SidebarMenuItem>
                  <SidebarMenuButton
                    asChild
                    isActive={location.pathname === dashboardPath}
                    tooltip={t('common:nav.dashboard')}
                  >
                    <NavLink to={dashboardPath} end onClick={() => setOpenMobile(false)}>
                      <LayoutDashboard className="size-4 shrink-0" />
                      <span>{t('common:nav.dashboard')}</span>
                    </NavLink>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              </SidebarMenu>
            </SidebarGroupContent>
          </CollapsibleContent>
        </Collapsible>
      </SidebarGroup>

      <SidebarGroup>
        <SidebarGroupLabel>{t('common:nav.inputs')}</SidebarGroupLabel>
        <SidebarGroupContent>
          <SidebarMenu>
            <SidebarMenuItem>
              <SidebarMenuButton
                asChild
                isActive={location.pathname === installationPrecursorCnPath}
                tooltip={t('common:nav.installationConfigurationPrecursorCn')}
              >
                <NavLink to={installationPrecursorCnPath} onClick={() => setOpenMobile(false)}>
                  <ListTree className="size-4 shrink-0" />
                  <span>{t('common:nav.installationConfigurationPrecursorCn')}</span>
                </NavLink>
              </SidebarMenuButton>
            </SidebarMenuItem>
            <SidebarMenuItem>
              <SidebarMenuButton
                asChild
                isActive={location.pathname === installationFuelsPath}
                tooltip={t('common:nav.installationConfigurationFuels')}
              >
                <NavLink to={installationFuelsPath} onClick={() => setOpenMobile(false)}>
                  <ListTree className="size-4 shrink-0" />
                  <span>{t('common:nav.installationConfigurationFuels')}</span>
                </NavLink>
              </SidebarMenuButton>
            </SidebarMenuItem>
            <SidebarMenuItem>
              <SidebarMenuButton
                asChild
                isActive={location.pathname === installationScrapCategoriesPath}
                tooltip={t('common:nav.installationConfigurationScrap')}
              >
                <NavLink to={installationScrapCategoriesPath} onClick={() => setOpenMobile(false)}>
                  <ListTree className="size-4 shrink-0" />
                  <span>{t('common:nav.installationConfigurationScrap')}</span>
                </NavLink>
              </SidebarMenuButton>
            </SidebarMenuItem>
            <SidebarMenuItem>
              <SidebarMenuButton
                asChild
                isActive={location.pathname === installationOtherMaterialPath}
                tooltip={t('common:nav.installationConfigurationOtherMaterial')}
              >
                <NavLink to={installationOtherMaterialPath} onClick={() => setOpenMobile(false)}>
                  <ListTree className="size-4 shrink-0" />
                  <span>{t('common:nav.installationConfigurationOtherMaterial')}</span>
                </NavLink>
              </SidebarMenuButton>
            </SidebarMenuItem>
          </SidebarMenu>
        </SidebarGroupContent>
      </SidebarGroup>

      <SidebarGroup>
        <SidebarGroupLabel>{t('common:nav.outputs')}</SidebarGroupLabel>
        <SidebarGroupContent>
          <SidebarMenu>
            <SidebarMenuItem>
              <SidebarMenuButton
                asChild
                isActive={location.pathname === installationProducedCnPath}
                tooltip={t('common:nav.installationConfigurationProducedCn')}
              >
                <NavLink to={installationProducedCnPath} onClick={() => setOpenMobile(false)}>
                  <ListTree className="size-4 shrink-0" />
                  <span>{t('common:nav.installationConfigurationProducedCn')}</span>
                </NavLink>
              </SidebarMenuButton>
            </SidebarMenuItem>
          </SidebarMenu>
        </SidebarGroupContent>
      </SidebarGroup>

      {(canManageOrganization || canManageUsers) && (
        <>
          <SidebarSeparator />
          <SidebarGroup>
            <SidebarGroupLabel>{t('common:nav.organization')}</SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                {canManageOrganization && (
                  <SidebarMenuItem>
                    <SidebarMenuButton
                      asChild
                      isActive={location.pathname === organizationCompanyPath}
                      tooltip={t('common:nav.organizationDetails')}
                    >
                      <NavLink to={organizationCompanyPath} onClick={() => setOpenMobile(false)}>
                        <Building2 className="size-4 shrink-0" />
                        <span>{t('common:nav.organizationDetails')}</span>
                      </NavLink>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                )}
                {canManageOrganization && (
                  <SidebarMenuItem>
                    <SidebarMenuButton
                      asChild
                      isActive={location.pathname === suppliersPath}
                      tooltip={t('common:nav.suppliers')}
                    >
                      <NavLink to={suppliersPath} onClick={() => setOpenMobile(false)}>
                        <Truck className="size-4 shrink-0" />
                        <span>{t('common:nav.suppliers')}</span>
                      </NavLink>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                )}
                {canManageUsers && (
                  <SidebarMenuItem>
                    <SidebarMenuButton
                      asChild
                      isActive={location.pathname === usersPath}
                      tooltip={t('common:nav.users')}
                    >
                      <NavLink to={usersPath} onClick={() => setOpenMobile(false)}>
                        <Users className="size-4 shrink-0" />
                        <span>{t('common:nav.users')}</span>
                      </NavLink>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                )}
                {canManageUsers && (
                  <SidebarMenuItem>
                    <SidebarMenuButton
                      asChild
                      isActive={location.pathname === organizationActivityPath}
                      tooltip={t('common:nav.organizationActivity')}
                    >
                      <NavLink to={organizationActivityPath} onClick={() => setOpenMobile(false)}>
                        <History className="size-4 shrink-0" />
                        <span>{t('common:nav.organizationActivity')}</span>
                      </NavLink>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                )}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        </>
      )}
    </>
  )
}
